<template>
    <SectionBase v-if="section.galleryItems.length" :section="section">
        <Component
            :is="activeGalleryComponent"
            :section="section"
            :items="galleryItems"
            :is-preview="isPreview"
        >
            <template #gallery-item="{ item, index, itemStyle, scrollHintPriority }">
                <GetGalleryMediaContent
                    :gallery-item="item"
                    :image-styles="{aspectRatio: section.mediaAspectRatio, cornerRadius: section.cornerRadiusPx}"
                    :index="index"
                    :scroll-hint-priority="scrollHintPriority"
                    :scroll-hint-section-key="section?._id"
                    :section="section"
                    :style="itemStyle"
                    :is-preview="isPreview"
                />
            </template>
            <template #caption="{ activeItem, alignLeft }">
                <GalleryCaption
                    :align-left="alignLeft"
                    :caption="activeItem?.media?.caption || ''"
                />
            </template>
        </Component>
    </SectionBase>
</template>

<script>
import SectionBase from "@/scripts/view/portfolio/sections/SectionBase.vue";
import GalleryGrid from "@/scripts/sections/modules/gallery/GalleryGrid.vue";
import GallerySlider from "@/scripts/sections/modules/gallery/GallerySlider.vue";
import GalleryCarousel from "@/scripts/sections/modules/gallery/GalleryCarousel.vue";
import GalleryTiltedLegacy from "@/scripts/view/portfolio/sections/modules/gallery/GalleryTiltedLegacy.vue";
import {GalleryLayout} from "@/scripts/common/enums.js";
import GetGalleryMediaContent from "@/scripts/view/portfolio/sections/_helper/GetGalleryMediaContent.vue";
import GalleryCaption from "@/scripts/view/portfolio/sections/modules/gallery/GalleryCaption.vue";
import {playAutoMockupScrollHint} from "@/scripts/common/utilities/mockupScrollHint.js";

export default {
    name: "Gallery",
    components: {
        SectionBase,
        GalleryGrid,
        GallerySlider,
        GalleryCarousel,
        GalleryTiltedLegacy,
        GetGalleryMediaContent,
        GalleryCaption
    },
    props: {
        section: {
            type: Object,
            default: null
        },
        isPreview: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            autoHintCheckScheduled: false
        };
    },
    mounted() {
        if (this.isPreview) return;

        window.addEventListener("scroll", this.scheduleAutoHintCheck, {passive: true});
        window.addEventListener("resize", this.scheduleAutoHintCheck);
        this.scheduleAutoHintCheck();
    },
    beforeUnmount() {
        window.removeEventListener("scroll", this.scheduleAutoHintCheck);
        window.removeEventListener("resize", this.scheduleAutoHintCheck);
    },
    computed: {
        galleryItems() {
            return this.section?.galleryItems || [];
        },
        activeGalleryComponent() {
            const components = {
                [GalleryLayout.GRID]: "GalleryGrid",
                [GalleryLayout.OFFSET_GRID]: "GalleryGrid",
                [GalleryLayout.SLIDER]: "GallerySlider",
                [GalleryLayout.CAROUSEL]: "GalleryCarousel",
                [GalleryLayout.TILTED]: "GalleryTiltedLegacy"
            };
            return components[this.section?.layout] || "GalleryGrid";
        }
    },
    methods: {
        scheduleAutoHintCheck() {
            if (this.autoHintCheckScheduled) return;
            this.autoHintCheckScheduled = true;

            requestAnimationFrame(() => {
                this.autoHintCheckScheduled = false;
                this.checkAutoHintTrigger();
            });
        },
        checkAutoHintTrigger() {
            if (this.isPreview || !this.$el) return;

            const rect = this.$el.getBoundingClientRect();
            const triggerY = window.innerHeight * 0.4;
            const reachedTriggerZone = rect.top <= triggerY;
            const stillVisible = rect.bottom > 0;

            if (!reachedTriggerZone || !stillVisible) return;

            const played = playAutoMockupScrollHint(this.section?._id);
            if (played) {
                window.removeEventListener("scroll", this.scheduleAutoHintCheck);
                window.removeEventListener("resize", this.scheduleAutoHintCheck);
            }
        }
    }
}
</script>
